import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const HighLevelSyncSettings = () => {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await api.get(`${backendUrl}/api/settings/highlevel-sync`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSettings(response.data.settings);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching HighLevel sync settings:', error);
      setMessage({ type: 'error', text: 'Failed to load sync settings' });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      const token = localStorage.getItem('access_token');
      const response = await api.put(
        `${backendUrl}/api/settings/highlevel-sync`,
        {
          outbound_sync_enabled: settings.outbound_sync_enabled,
          inbound_sync_enabled: settings.inbound_sync_enabled,
          sync_filter: settings.sync_filter
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSettings(response.data.settings);
      setMessage({ type: 'success', text: 'Sync settings saved successfully!' });
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save sync settings' });
    } finally {
      setSaving(false);
    }
  };

  const formatLastSync = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Failed to load HighLevel sync settings
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">HighLevel Task Sync</h2>
          <p className="text-sm text-gray-600 mt-1">
            Automatically sync tasks between Athena and GoHighLevel
          </p>
        </div>
        
        {stats && (
          <div className="text-right">
            <div className="text-sm text-gray-600">Last sync</div>
            <div className="text-sm font-medium text-gray-900">
              {formatLastSync(stats.last_sync_time)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {stats.total_synced_tasks} tasks synced
            </div>
          </div>
        )}
      </div>

      {message && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-700' 
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      <div className="space-y-6">
        {/* Sync Toggles */}
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="outbound-sync"
                type="checkbox"
                checked={settings.outbound_sync_enabled}
                onChange={(e) => setSettings({ ...settings, outbound_sync_enabled: e.target.checked })}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="outbound-sync" className="font-medium text-gray-900">
                Enable Outbound Sync (Athena → HighLevel)
              </label>
              <p className="text-sm text-gray-600">
                When a task is moved to the last stage in Athena, mark it as complete in HighLevel
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="inbound-sync"
                type="checkbox"
                checked={settings.inbound_sync_enabled}
                onChange={(e) => setSettings({ ...settings, inbound_sync_enabled: e.target.checked })}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="inbound-sync" className="font-medium text-gray-900">
                Enable Inbound Sync (HighLevel → Athena)
              </label>
              <p className="text-sm text-gray-600">
                When a task is created or completed in HighLevel, sync it to Athena's "General" project
              </p>
            </div>
          </div>
        </div>

        {/* Sync Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Task Sync Filter
          </label>
          <select
            value={settings.sync_filter}
            onChange={(e) => setSettings({ ...settings, sync_filter: e.target.value })}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all_clients">Sync All Tasks</option>
            <option value="tagged_only">Sync Client Tasks (In HighLevel Task associated to contact with Athena AI tag)</option>
          </select>
          <p className="mt-1 text-sm text-gray-600">
            Choose which HighLevel tasks should be synced to Athena
          </p>
        </div>

        {/* Sync Behavior Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">How Sync Works</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Outbound:</strong> Tasks moved to last stage → Marked complete in HighLevel</li>
            <li>• <strong>Inbound:</strong> HL task with contact → Client's "General" project</li>
            <li>• <strong>Inbound:</strong> HL task without contact → Company Projects "General"</li>
            <li>• <strong>Inbound:</strong> HL task completed → Moved to last stage in Athena</li>
            <li>• <strong>Assignees:</strong> Automatically matched by email address</li>
          </ul>
        </div>

        {/* Sync Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">Total Synced</div>
              <div className="text-2xl font-semibold text-gray-900">{stats.total_synced_tasks}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-700">Active</div>
              <div className="text-2xl font-semibold text-green-900">{stats.synced_count}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-sm text-red-700">Errors</div>
              <div className="text-2xl font-semibold text-red-900">{stats.error_count}</div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HighLevelSyncSettings;
